import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import citiesData from "@/data/citiesBoard.json";
import { CitiesBoard } from "./CitiesBoard";
import { Card } from "@/design/primitives";
import { AuthGate } from "@/screens/cc";


// TJB City Projects wall (canonical path /tjb/cities, Jeff 2026-10-21):
// live data from the Kit mirror, baked fallback from citiesBoard.json,
// signed-out read-only, live-only notes. /cities redirects here — the
// main-menu Cities entry was removed; this page lives only under the
// True Joy Birthing brand.
export default function CitiesBoardPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const board = useBoard("cities");
  const [dismissedSample, setDismissedSample] = useState(false);

  const liveItems = useMemo(() => (board.items || []), [board.items]);
  const bakedItems = useMemo(() => citiesData.cards || [], []);

  const viewItems = board.live && liveItems.length ? liveItems : bakedItems;
  const viewMeta = useMemo(() => {
    if (board.live && board.meta?.ingested_at) return board.meta;
    return citiesData.stamp;
  }, [board.live, board.meta]);

  const total = viewItems.length;
  const done = viewMeta?.done_count ?? 0;
  const building = viewMeta?.building_count ?? 0;
  const blocked = viewMeta?.blocked_count ?? 0;

  return (
    <div data-testid="cities-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {board.status === "needs-signin" ? (
          <>
            <AuthGate />
            <CitiesBoard items={bakedItems} meta={citiesData.stamp} variant={desktop ? "desktop" : "mobile"} />
          </>
        ) : (
          <>
            {board.live && !dismissedSample && !board.meta?.ingested_at && (
              <button
                type="button"
                data-testid="cities-sample-note"
                onClick={() => setDismissedSample(true)}
                className="self-start rounded-control border border-os-border px-3 py-1.5 text-left font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink"
              >
                Today these cities are a hand-built sample — Kit's live TJB pipeline data arrives on the first fetch. Tap to dismiss.
              </button>
            )}
            <CitiesBoard items={viewItems} meta={viewMeta} variant={desktop ? "desktop" : "mobile"} board={board} />
            {!board.live && (
              <Card quiet className="px-5 py-3">
                <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
                  Kit's city pipeline is offline this visit — the page shows the last committed state.
                </p>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
